/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useMemo } from "react";
import { Stack, Theme, useTheme } from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { motion } from "framer-motion";
import { HeatSeries } from "src/store/api/generatedApi";
import { useTenantTranslation } from "hooks/formatters";
import { defaultLayout } from "../../plotting";
import { Layout, PlotData } from "plotly.js";
import { useChemistryElement } from "./ChemistryElementContext";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { TFunction } from "i18next";

interface Props {
  heatData: HeatSeries;
}

interface ChemistryDataPoint {
  timestamp: string;
  constraintName: string;
  weightPercent: number;
  weightPercentMin: number;
  weightPercentMax: number;
  violationFraction: number;
  sampleLocation: string;
}

export const getToleranceLines = (
  chemistryData: ChemistryDataPoint[],
  timestamps: string[],
  toLocalDatetime: (date: string, format: string) => string,
  t: TFunction,
  theme: Theme
) => {
  let minValues = chemistryData.map((p) => p.weightPercentMin).filter(Boolean);
  let maxValues = chemistryData.map((p) => p.weightPercentMax).filter(Boolean);
  const hasMinValues = minValues.length > 0;
  const hasMaxValues = maxValues.length > 0;

  // A tolerance line over a single timestamp has zero length and is invisible.
  const pointAtSharedTimestamp =
    new Set(timestamps).size === 1 ? chemistryData[0] : undefined;
  if (pointAtSharedTimestamp) {
    const originalTime = new Date(pointAtSharedTimestamp.timestamp);
    const from = new Date(originalTime.getTime() - 60000); // -1 minute
    const to = new Date(originalTime.getTime() + 60000); // +1 minute
    timestamps = [from, to].map((time) =>
      toLocalDatetime(time.toISOString(), "YYYY-MM-DD HH:mm:ss")
    );
    if (hasMinValues) {
      minValues = [
        pointAtSharedTimestamp.weightPercentMin,
        pointAtSharedTimestamp.weightPercentMin,
      ];
    }
    if (hasMaxValues) {
      maxValues = [
        pointAtSharedTimestamp.weightPercentMax,
        pointAtSharedTimestamp.weightPercentMax,
      ];
    }
  }
  const traces: Partial<PlotData>[] = [
    ...(hasMinValues
      ? [
          {
            x: timestamps,
            y: minValues,
            type: "scatter",
            mode: "lines",
            name: t("min"),
            line: { color: theme.palette.data.red, dash: "dashdot" },
            hoverinfo: "skip",
          } as Partial<PlotData>,
        ]
      : []),
    ...(hasMaxValues
      ? [
          {
            x: timestamps,
            y: maxValues,
            type: "scatter",
            mode: "lines",
            name: t("max"),
            line: { color: theme.palette.data.red, dash: "dash" },
            hoverinfo: "skip",
          } as Partial<PlotData>,
        ]
      : []),
  ];
  return traces;
};

export const HeatChemistryOverTime = ({ heatData }: Props) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { toLocalDatetime } = useLocalDatetime();
  const { selectedElement, selectedElementColor } = useChemistryElement();

  const chemistryData = useMemo((): ChemistryDataPoint[] => {
    const dataPoints: ChemistryDataPoint[] = [];

    if (heatData.samples && selectedElement) {
      heatData.samples.forEach((sample) => {
        sample.values?.forEach((value) => {
          if (value.constraint_name === selectedElement) {
            dataPoints.push({
              timestamp: sample.sample_timestamp,
              constraintName: value.constraint_name,
              weightPercent: value.weight_percent,
              weightPercentMin: value.weight_percent_min,
              weightPercentMax: value.weight_percent_max,
              violationFraction: value.violation_fraction,
              sampleLocation: sample.sample_location,
            });
          }
        });
      });
    }

    return dataPoints.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [heatData.samples, selectedElement]);

  const plotData = useMemo((): Partial<PlotData>[] => {
    if (!selectedElement || chemistryData.length === 0) {
      return [];
    }

    const timestamps = chemistryData.map((p) =>
      toLocalDatetime(p.timestamp, "YYYY-MM-DD HH:mm:ss")
    );
    const onlyOneSample = chemistryData.length === 1;
    const traces: Partial<PlotData>[] = [
      {
        x: timestamps,
        y: chemistryData.map((p) => p.weightPercent),
        type: "scatter",
        mode: "lines+markers",
        name: t("measured"),
        line: { color: selectedElementColor },
        marker: { size: onlyOneSample ? 12 : 6 }, // if only one sample, make it bigger
        hovertemplate:
          "%{x|%b %d, %Y %H:%M}<br>" +
          t("measured") +
          ": %{y:.4f}%<br>" +
          t("sampleLocation") +
          ": %{customdata}<br>" +
          "<extra></extra>",
        customdata: chemistryData.map((p) => p.sampleLocation),
      },
      ...getToleranceLines(
        chemistryData,
        timestamps,
        toLocalDatetime,
        t,
        theme
      ),
    ];

    return traces;
  }, [
    chemistryData,
    selectedElement,
    selectedElementColor,
    t,
    theme,
    toLocalDatetime,
  ]);

  const verticalLines = useMemo(
    () =>
      chemistryData.length > 0
        ? chemistryData
            .filter((point, index) => {
              if (index === 0) return false;
              return (
                point.sampleLocation !==
                chemistryData[index - 1]?.sampleLocation
              );
            })
            .flatMap((point) => {
              const currentIndex = chemistryData.findIndex((p) => p === point);
              const previousPoint = chemistryData[currentIndex - 1];

              if (!previousPoint) return [];

              const currentTime = new Date(point.timestamp).getTime();
              const previousTime = new Date(previousPoint.timestamp).getTime();
              const midpointTime = toLocalDatetime(
                new Date((currentTime + previousTime) / 2).toISOString(),
                "YYYY-MM-DD HH:mm:ss"
              );

              return {
                x: midpointTime,
                leftText: t(previousPoint.sampleLocation),
                rightText: t(point.sampleLocation),
              };
            })
        : [],
    [chemistryData, t, toLocalDatetime]
  );

  const layout: Partial<Layout> = useMemo(
    () => ({
      ...defaultLayout,
      xaxis: {
        type: "date",
        title: { text: t("time") },
      },
      yaxis: {
        ...defaultLayout.yaxis,
        title: { text: `${t("weightPercent")} (%)` },
        tickformat: ".4f",
      },
      legend: {
        ...defaultLayout.legend,
        orientation: "h",
        y: -0.2,
        xanchor: "center",
      },
      height: 500,
      shapes: verticalLines.map(({ x }) => ({
        type: "line" as const,
        x0: x,
        x1: x,
        y0: 0,
        y1: 1,
        yref: "paper" as const,
        line: {
          color: theme.palette.grey[500],
          width: 3,
          dash: "dot",
        },
      })),
      annotations: [
        ...verticalLines.map(({ x, leftText }) => ({
          x: x,
          y: 0.9,
          text: leftText,
          textangle: "-90",
          showarrow: false,
          yref: "paper" as const,
          xanchor: "right" as const,
          yanchor: "top" as const,
        })),
        ...verticalLines.map(({ x, rightText }) => ({
          x: x,
          y: 0.9,
          text: rightText,
          textangle: "90",
          showarrow: false,
          yref: "paper" as const,
          xanchor: "left" as const,
          yanchor: "top" as const,
        })),
      ],
    }),
    [t, theme, verticalLines]
  );

  return (
    <Stack flex={1}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div style={{ width: "100%" }}>
          <Plot
            data={plotData}
            layout={layout}
            style={{ width: "100%" }}
            useResizeHandler
          />
        </div>
      </motion.div>
    </Stack>
  );
};
